<template>
  <GroupReservationHead :reservation="reservation" :selectedGroup="selectedGroup" :order="order" />

  <div class="py-0">
    <Tags :tags="reservation?.tags" />
    <div class="text-gray-700 text-base mt-4 mb-6"
      v-html="selectedGroup?.reservationDescription + '<br>' + selectedGroup?.description"></div>

    <div class="group-selector pt-4 pb-2">
      <label for="group-select" class="block text-sm font-medium text-gray-700">Wybrana grupa</label>

      <div>
        <select id="group-select" v-model="selectedGroup" @change="selectGroup(selectedGroup)"
          :class="`bg-gray-50 h-14 mt-1 block w-full border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 ${roundedClass} ${groups.length === 1 ? 'appearance-none' : ''}`"
          :disabled="groups.length === 1" :style="groups.length === 1 ? 'background-image: none;' : ''">
          <option v-for="group in groups" :key="group.id" :value="group">
            {{ group.name }}
          </option>
        </select>
      </div>
    </div>



    <div v-if="(selectedGroup?.noAvailability && eligible)" class="pt-4 pb-2">

      <PlaceholderContact :class="roundedClass" title="Lista rezerwowa" :contactInfo="`<a href='mailto:kontakt.akademiaimprowizacji@gmail.com?subject=Lista%20rezerwowa%20${selectedGroup?.name}&body=Proszę%20zapisać%20mnie%20na%20listę%20rezerwową%20${selectedGroup?.name}' class='inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-neutral-950 rounded-full text-sm font-bold transition-colors'><svg class='w-4 h-4 hrink-0' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path d='M12 5.33333C12 4.27247 11.5786 3.25505 10.8284 2.50491C10.0783 1.75476 9.06087 1.33333 8 1.33333C6.93913 1.33333 5.92172 1.75476 5.17157 2.50491C4.42143 3.25505 4 4.27247 4 5.33333C4 10 2 11.3333 2 11.3333H14C14 11.3333 12 10 12 5.33333Z' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/>
              <path d='M9.15333 14C9.03614 14.2021 8.86791 14.3698 8.6654 14.4864C8.46289 14.6029 8.2334 14.6643 8 14.6643C7.7666 14.6643 7.53711 14.6029 7.3346 14.4864C7.13209 14.3698 6.96386 14.2021 6.84667 14' stroke='currentColor' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/>
          </svg>
          <span> Dołącz do listy oczekujących</span>
            </a>`" />
    </div>

    <div v-if="!selectedGroup?.noAvailability && eligible" class="pt-4 pb-2">
      <CountSelector :order="order" />
    </div>

    <ReqBox v-if="selectedGroup?.hasEarlyBirds || selectedGroup?.hasRequirements" :key="selectedGroup?.id"
      :group="selectedGroup" :user="currentUser" class="mb-4 mt-4" @eligible="handleEligible"
      @eligibleEarlyBird="onEarlyBirdEligibility" />

    <NextButton :onNext="resolvedOnNext" :onPrev="onPrev" :canProceed="canProceedFunction"
      :onNextTitle="nextButtonTitle" />


    <Summary :order="order" class="lg:hidden" />
  </div>
</template>

<script>
import Globals from '@shared/model/Globals.js';
import GroupReservationHead from './GroupReservationHead.vue';
import Tags from '@/components/Tags.vue';
import CountSelector from '@/components/CountSelector.vue';
import Summary from '@/components/Summary.vue';
import NextButton from '@/components/NextButton.vue';
import PlaceholderContact from '@/components/PlaceholderContact.vue';
import ReqBox from './ReqBox.vue';
import rpAPI from '@shared/service/rpAPI.js';

export default {
  name: 'GroupSelector',
  components: {
    GroupReservationHead,
    Tags,
    CountSelector,
    Summary,
    NextButton,
    PlaceholderContact,
    ReqBox
  },
  props: {
    reservation: { type: Object, required: false, default: null },
    groups: { type: Array, required: true },
    order: { type: Object, required: false, default: null },
    onNext: { type: Function, required: false },
    onCancel: { type: Function, required: false },
    onPrev: { type: Function, required: false }
  },
  emits: ['update:selectedGroup', 'eligibility', 'earlyBirdEligibility'],
  data() {
    return {
      earlyBirdEligible: false,
      eligible: true,
    };
  },
  computed: {
    selectedGroup: {
      get() {
        return this.order?.selectedGroup ?? null;
      },
      set(val) {
        if (this.order && typeof this.order.setGroup === 'function') {
          this.order.setGroup(val ?? null);
        }
        this.$emit('update:selectedGroup', val ?? null);
      }
    },
    defaultProfile() {
      if (this.order?.customer && typeof this.order.customer.defaultProfile === 'function') {
        return this.order.customer.defaultProfile();
      }
      return null;
    },

    currentUser() {
      return rpAPI.user ?? null;
    },

    // Redirect to login when early birds required and user is not logged in
    needsLogin() {
      return this.selectedGroup?.hasEarlyBirds && !rpAPI.user;
    },

    nextButtonTitle() {
      return this.needsLogin ? 'Zaloguj się' : 'Dalej';
    },

    canProceed() {
      // Rule 1: at least one item in order
      if (!this.order || this.order.getCount() <= 0) return false;
      // Rule 2: group must have free places and not have started
      if (this.selectedGroup?.noAvailability) return false;

      if (this.selectedGroup?.hasEarlyBirds) {
        // Rule 3 & 4: early birds bypass enrollment start; skip requirements check
        if (!this.earlyBirdEligible) return false;
      } else {
        // Rule 5: check enrollment start and requirements
        if (this.order.isBeforeEnrollmentStart()) return false;
        if (this.selectedGroup?.hasRequirements && !this.eligible) return false;
      }

      return true;
    },
    roundedClass() {
      return Globals.getRoundedClass();
    }
  },
  watch: {
    groups: {
      immediate: true,
      handler(val) {
        if (Array.isArray(val) && val.length === 1 && !this.selectedGroup) {
          this.selectedGroup = val[0];
        }
      }
    }
  },
  methods: {
    resolvedOnNext() {
      if (this.needsLogin) {
        const redirect = encodeURIComponent(window.location.href);
        window.location.href = `/authorization?redirect=${redirect}`;
        return;
      }
      if (this.onNext) this.onNext();
    },
    onEarlyBirdEligibility(val) {
      this.earlyBirdEligible = !!val;
      this.order.setIsEligibleEarlyBird(!!val);
    }, 
    handleEligible(val) {
      this.eligible = val ?? true;
    },

    selectGroup(group) {
      this.selectedGroup = group;
    },
    canProceedFunction() {
      return this.canProceed;
    }
  }
};
</script>

<style scoped>
/* add styles if needed */
</style>